import {
  DRAWING_LINE_WIDTH,
  asIsoDate,
  formatPercent,
  formatPrice,
  formatSigned,
  priceDistance,
} from '@stockstop/shared';
import {
  applyStroke,
  distanceToPoint,
  drawHandle,
  drawLabel,
  HANDLE_HIT_RADIUS,
  HIT_TOLERANCE,
} from './geometry.js';
import type { DrawingRenderer } from './types.js';

/**
 * (1) Stop-Loss-Linie: eine waagerechte Marke auf dem Stop-Kurs.
 *
 * Als einzige Zeichnung trägt sie ihre Beschriftung **dauerhaft**, nicht nur beim
 * Überfahren: Sie zeigt den Kurs, an dem die Position verkauft würde. Diese Zahl
 * ist der Zweck der Anwendung und darf nicht erst auf Nachfrage erscheinen.
 *
 * (1.1) Mit `distanceLeg` läuft eine senkrechte Verbindung von der Linie zum
 * letzten Schlusskurs, beschriftet mit dem Abstand absolut und in Prozent. Ihre
 * x-Position wird bei jedem Frame aus der jüngsten Kerze abgeleitet statt
 * gespeichert, sodass sie mitwandert, wenn neue Kurse hinzukommen.
 *
 * Der Abstand stammt aus `priceDistance` — derselben Funktion, mit der der Server
 * den Stop-Loss auswertet. Chart und Tabelle können daher nicht abweichen.
 */
export const stopLossLineRenderer: DrawingRenderer<'stop_loss'> = {
  type: 'stop_loss',

  handles(payload, context) {
    const y = context.coordinates.yForPrice(payload.price);
    if (y === null) return [];
    /* Anchored to the left so it never collides with the price-axis labels. */
    return [{ id: 'price', position: { x: 64, y } }];
  },

  draw(payload, style, label, showPrice, context) {
    const { ctx, coordinates, width } = context;
    const y = coordinates.yForPrice(payload.price);
    if (y === null) return;

    ctx.save();
    /*
     * Ausdrücklich volle Deckkraft: Die Stop-Loss-Linie zeigt den Kurs, zu dem
     * verkauft würde, und darf nicht hinter anderes zurücktreten. `style.opacity`
     * wird hier bewusst nicht angewandt.
     */
    ctx.globalAlpha = 1;
    applyStroke(
      ctx,
      style.color,
      DRAWING_LINE_WIDTH.stop_loss + (context.isSelected ? 1 : 0),
      style.lineStyle,
    );
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(width, y);
    ctx.stroke();

    /* (1.1) Vertical leg from the line up or down to the latest close. */
    if (payload.distanceLeg && context.latestClose !== null && context.latestDate !== null) {
      const legX = coordinates.xForDate(asIsoDate(context.latestDate));
      const closeY = coordinates.yForPrice(context.latestClose);

      if (legX !== null && closeY !== null) {
        applyStroke(ctx, style.color, 1, 'dashed');
        ctx.beginPath();
        ctx.moveTo(legX, y);
        ctx.lineTo(legX, closeY);
        ctx.stroke();

        /* Small end caps make the measured span unambiguous. */
        ctx.beginPath();
        ctx.moveTo(legX - 4, closeY);
        ctx.lineTo(legX + 4, closeY);
        ctx.stroke();

        const { absolute, percent } = priceDistance(context.latestClose, payload.price);
        const gap = `${formatSigned(-absolute)} (${percent === null ? '–' : formatPercent(-percent)})`;
        drawLabel(
          ctx,
          gap,
          { x: legX - 8, y: (y + closeY) / 2 + 10 },
          {
            color: style.color,
            align: 'right',
          },
        );
      }
    }

    const caption = [
      label,
      showPrice ? formatPrice(payload.price, { currency: context.currency }) : null,
    ]
      .filter(Boolean)
      .join('  ');
    drawLabel(ctx, caption, { x: 8, y: y - 4 }, { color: style.color });

    if (context.isSelected) {
      for (const handle of this.handles(payload, context)) {
        drawHandle(ctx, handle.position, style.color);
      }
    }
    ctx.restore();
  },

  hitTest(payload, point, context) {
    const y = context.coordinates.yForPrice(payload.price);
    if (y === null) return null;

    const handle = this.handles(payload, context)[0];
    if (handle && distanceToPoint(point, handle.position) <= HANDLE_HIT_RADIUS) return 'price';
    if (Math.abs(point.y - y) <= HIT_TOLERANCE) return 'body';
    return null;
  },

  applyDrag(payload, _handle, drag) {
    /* Both the body and the handle do the same thing: move the level. */
    if (drag.price <= 0) return null;
    return { ...payload, price: drag.price };
  },
};
